import { useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card } from '../components/Card'
import { Icon } from '../components/Icon'
import { useCurrency } from '../lib/currency-context'
import { fmtMoney, FMT_INT } from '../lib/money'
import { CopyCell } from '../components/CopyCell'

interface BulkResult {
  tracking:        string
  found:           boolean
  tracking_number:        string | null
  master_tracking_number: string | null
  carrier:         string | null
  customer:        string | null
  ship_date:       string | null
  bl_cost:         number | null
  bl_profit:       number | null
  bl_tax:          number | null
  bl_total:        number | null
  carrier_transport: number | null
  carrier_duty:      number | null
  carrier_total:     number | null
  transport_pl:      number | null
  duty_pl:           number | null
  total_pl:          number | null
  apv_adjustment:  number | null
  dispute_status:  string | null
  rebill_status:   string | null
  invoiced:        boolean
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface ShipmentRow {
  tracking_number:        string
  master_tracking_number: string | null
  customer_name:          string | null
  customer_display_name:  string | null
  carrier_service:        string | null
  carrier_account:        string | null
  create_date:            string | null
  currency_code:          string
  label_cost_minor:       number
  label_cost_usd_minor:   number
  total_cost_usd_minor:   number
  profit_usd_minor:       number
  sender_country_code:    string | null
  recipient_country_code: string | null
  package_weight_lb:      number | null
}

interface CarrierCharge {
  carrier:         string
  tracking_number: string
  invoice_date:    string | null
  transport:       number | string | null
  duty_tax:        number | string | null
  charge_amount:   number | string | null
  charge_currency: string
  service_type:    string | null
  invoice_id:      string | null
}

interface RebillRow {
  rebill_id:              number
  carrier:                string
  adjustment_code:        string
  adjustment_category:    string
  adjustment_name:        string | null
  cost_amount:            number
  markup_pct:             number
  rebill_amount:          number
  status:                 string
  invoice_number:         string | null
  carrier_invoice_number: string | null
  carrier_invoice_date:   string | null
  rebill_reason:          string
  detected_at:            string
}

interface CensusRow {
  eps_transaction_id:      string | null
  efn:                     string | null
  transaction_date:        string | null
  details:                 string | null
  manifest_mail_class:     string | null
  assessed_mail_class:     string | null
  manifest_weight_lbs:     number | null
  assessed_weight_lbs:     number | null
  dimensional_weight_lbs:  number | null
  manifest_length:  number | null; manifest_width:  number | null; manifest_height:  number | null
  assessed_length:  number | null; assessed_width:  number | null; assessed_height:  number | null
  manifested_zone:  string | null; assessed_zone:   string | null
  weight_scan_source:      string | null
  dimensions_scan_source:  string | null
  paid_postage:      number | null
  assessed_postage:  number | null
  transaction_amount: number | null
}

interface DisputeRow {
  dispute_id:       number
  carrier:          string
  status:           string
  disputed_amount:  number
  dispute_category: string
  dispute_reason:   string
  credit_amount:    number | null
  submitted_at:     string | null
  resolved_at:      string | null
  detected_at:      string
}

interface LookupResult {
  tracking:        string
  shipment:        ShipmentRow | null
  carrier_charges: CarrierCharge[]
  rebills:         RebillRow[]
  usps_census:     CensusRow[]
  disputes:        DisputeRow[]
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function statusBadge(status: string) {
  const cls: Record<string, string> = {
    DRAFT:     'bg-ink-100 text-ink-600',
    PENDING:   'bg-amber-100 text-amber-700',
    INVOICED:  'bg-royal-100 text-royal-700',
    PAID:      'bg-mint-100 text-mint-700',
    VOID:      'bg-red-100 text-red-600',
    DISPUTED:  'bg-amber-100 text-amber-700',
    SUBMITTED: 'bg-royal-100 text-royal-700',
    ACCEPTED:  'bg-mint-100 text-mint-700',   // dispute won (carrier waived)
    REJECTED:  'bg-red-100 text-red-600',     // dispute lost (upheld)
    CREDITED:  'bg-sky-100 text-sky-700',     // partial credit
    WITHDRAWN: 'bg-ink-100 text-ink-600',     // not disputed (closed by us, billed in full)
  }
  return (
    <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${cls[status] ?? 'bg-ink-100 text-ink-600'}`}>
      {status === 'WITHDRAWN' ? 'NOT DISPUTED' : status}
    </span>
  )
}

function fmtCurrencyAmount(amount: number | string | null, currency: string): string {
  if (amount === null || amount === undefined) return '—'
  // pg returns NUMERIC as a string; String.toLocaleString ignores the digit
  // options, so coerce to a real number before formatting (else "8.0500").
  const n = Number(amount)
  if (Number.isNaN(n)) return '—'
  const sym = currency === 'GBP' ? '£' : '$'
  return `${sym}${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function n(v: number | null | undefined): string {
  return v == null ? '—' : Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 })
}
function usd(v: number | null | undefined): string {
  return v == null ? '—' : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}
function dimsOf(l: number | null, w: number | null, h: number | null): string {
  return (l == null && w == null && h == null) ? '—' : `${n(l)}×${n(w)}×${n(h)}`
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between py-2 border-b border-ink-100 last:border-0 gap-4">
      <span className="text-[12px] text-ink-500 shrink-0">{label}</span>
      <span className="text-[13px] text-ink-900 text-right font-medium">{value ?? '—'}</span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main view
// ---------------------------------------------------------------------------
// ===========================================================================
// Per-shipment detail — rendered inside the drawer. All the cards live here.
// ===========================================================================
function ShipmentDetail({ tracking }: { tracking: string }) {
  const { currency } = useCurrency()

  const { data, isLoading, isError, error } = useQuery<LookupResult>({
    queryKey: ['lookup', tracking],
    queryFn:  () => fetch(`/api/lookup/${encodeURIComponent(tracking)}`).then(r => {
      if (!r.ok) throw new Error(`${r.status}`)
      return r.json()
    }),
    enabled:  tracking.trim().length >= 5,
    staleTime: 60_000,
  })

  const ship    = data?.shipment
  const charges = data?.carrier_charges ?? []
  const rebills = data?.rebills ?? []
  const census  = data?.usps_census ?? []
  const disputes = data?.disputes ?? []

  // Profit margin from byelabel shipment
  const margin = ship && ship.total_cost_usd_minor > 0
    ? ((ship.profit_usd_minor / ship.total_cost_usd_minor) * 100).toFixed(1)
    : null

  // Total carrier charges found
  const totalCarrierUsd = charges
    .filter(c => c.charge_currency === 'USD' && c.charge_amount !== null)
    .reduce((s, c) => s + Number(c.charge_amount ?? 0), 0)

  return (
    <div className="p-5 space-y-6">
      {isLoading && (
        <div className="text-[14px] text-ink-500 text-center py-12">Searching…</div>
      )}

      {/* Error */}
      {isError && (
        <div className="text-[14px] text-red-600 text-center py-12">
          Error: {(error as Error).message}
        </div>
      )}

      {/* Results */}
      {data && !isLoading && (
        <div className="space-y-6">

          {/* ── ByeLabel Shipment ── */}
          <section>
            <h2 className="text-[15px] font-bold text-ink-800 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-royal-500 inline-block" />
              ByeLabel Shipment
            </h2>
            {!ship ? (
              <Card className="p-6 text-[13px] text-ink-500 italic">
                No ByeLabel shipment record found for <span className="font-mono not-italic text-ink-700">{data.tracking}</span>.
                {charges.length > 0 && ' This is a ghost charge — the carrier billed ByeLabel for a shipment with no internal record.'}
              </Card>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {/* Identity */}
                <Card className="p-5">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-400 mb-3">Shipment Details</div>
                  <Row label="Tracking"   value={<CopyCell value={ship.tracking_number} />} />
                  {ship.master_tracking_number && (
                    <Row label="Master tracking" value={<CopyCell value={ship.master_tracking_number} />} />
                  )}
                  <Row label="Customer"    value={ship.customer_display_name ?? ship.customer_name} />
                  <Row label="Carrier"     value={ship.carrier_service} />
                  <Row label="Account"     value={ship.carrier_account} />
                  <Row label="Ship date"   value={ship.create_date ? ship.create_date.slice(0, 10) : null} />
                  <Row label="From → To"   value={[ship.sender_country_code, ship.recipient_country_code].filter(Boolean).join(' → ') || null} />
                  {ship.package_weight_lb != null && (
                    <Row label="Weight"    value={`${Number(ship.package_weight_lb).toFixed(2)} lb`} />
                  )}
                </Card>

                {/* Financials */}
                <Card className="p-5">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-400 mb-3">Financials</div>
                  <Row
                    label="Charged to customer"
                    value={<span className="font-semibold text-ink-900">{fmtMoney(ship.total_cost_usd_minor, currency)}</span>}
                  />
                  <Row
                    label="Label cost (USD)"
                    value={<span className="text-flame-600">{fmtMoney(ship.label_cost_usd_minor, currency)}</span>}
                  />
                  <Row
                    label="Profit"
                    value={
                      <span className={ship.profit_usd_minor >= 0 ? 'text-mint-700 font-semibold' : 'text-red-600 font-semibold'}>
                        {fmtMoney(ship.profit_usd_minor, currency)}
                      </span>
                    }
                  />
                  {margin !== null && (
                    <Row
                      label="Margin"
                      value={
                        <span className={Number(margin) >= 0 ? 'text-mint-700 font-semibold' : 'text-red-600 font-semibold'}>
                          {margin}%
                        </span>
                      }
                    />
                  )}
                  <Row label="Label currency" value={ship.currency_code} />
                  {ship.currency_code !== 'USD' && (
                    <Row
                      label="Label cost (native)"
                      value={fmtCurrencyAmount(ship.label_cost_minor / 100, ship.currency_code)}
                    />
                  )}

                  {/* Carrier charges total for comparison */}
                  {charges.length > 0 && totalCarrierUsd > 0 && (
                    <>
                      <div className="border-t border-ink-100 mt-2 pt-2">
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-600 mb-2">Carrier Invoice Comparison</div>
                      </div>
                      <Row
                        label="Total carrier billed (USD)"
                        value={<span className="text-amber-700 font-semibold">${totalCarrierUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>}
                      />
                      <Row
                        label="Difference (label cost − billed)"
                        value={(() => {
                          const diff = (ship.label_cost_usd_minor / 100) - totalCarrierUsd
                          return (
                            <span className={diff >= 0 ? 'text-mint-700 font-semibold' : 'text-red-600 font-semibold'}>
                              {diff >= 0 ? '+' : ''}{diff.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2, style: 'currency', currency: 'USD' })}
                            </span>
                          )
                        })()}
                      />
                    </>
                  )}
                </Card>
              </div>
            )}
          </section>

          {/* ── Carrier Charges ── */}
          <section>
            <h2 className="text-[15px] font-bold text-ink-800 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />
              Carrier Invoice Charges
              {charges.length > 0 && (
                <span className="ml-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[11px] font-bold">
                  {charges.length}
                </span>
              )}
            </h2>
            {charges.length === 0 ? (
              <Card className="p-6 text-[13px] text-ink-500 italic">
                No carrier invoice records found for this tracking number.
              </Card>
            ) : (
              <Card className="overflow-hidden">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="bg-ink-50 border-b border-ink-200">
                      <th className="text-left px-4 py-3 font-semibold text-ink-600">Carrier</th>
                      <th className="text-left px-4 py-3 font-semibold text-ink-600">Invoice Date</th>
                      <th className="text-right px-4 py-3 font-semibold text-ink-600">Transport</th>
                      <th className="text-right px-4 py-3 font-semibold text-ink-600">Duty / Tax</th>
                      <th className="text-right px-4 py-3 font-semibold text-ink-600">Total</th>
                      <th className="text-left px-4 py-3 font-semibold text-ink-600">Service</th>
                      <th className="text-left px-4 py-3 font-semibold text-ink-600">Invoice ID</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-100">
                    {charges.map((c, i) => (
                      <tr key={i} className="hover:bg-ink-50">
                        <td className="px-4 py-2.5 font-medium text-ink-800">{c.carrier}</td>
                        <td className="px-4 py-2.5 text-ink-600">{c.invoice_date ? c.invoice_date.slice(0, 10) : '—'}</td>
                        <td className="px-4 py-2.5 text-right num text-ink-700">
                          {c.transport == null ? '—' : fmtCurrencyAmount(c.transport, c.charge_currency)}
                        </td>
                        <td className="px-4 py-2.5 text-right num text-ink-700">
                          {c.duty_tax == null ? '—' : fmtCurrencyAmount(c.duty_tax, c.charge_currency)}
                        </td>
                        <td className="px-4 py-2.5 text-right num font-semibold text-ink-900">
                          {fmtCurrencyAmount(c.charge_amount, c.charge_currency)}
                        </td>
                        <td className="px-4 py-2.5 text-ink-500 max-w-[200px] truncate">{c.service_type ?? '—'}</td>
                        <td className="px-4 py-2.5 font-mono text-[11px] text-ink-400">{c.invoice_id ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                  {charges.length > 1 && totalCarrierUsd > 0 && (
                    <tfoot>
                      <tr className="bg-ink-50 border-t border-ink-200 font-semibold">
                        <td className="px-4 py-2.5 text-ink-600" colSpan={2}>Total (USD only)</td>
                        <td className="px-4 py-2.5 text-right num text-ink-900">
                          ${totalCarrierUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td colSpan={2} />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </Card>
            )}
          </section>

          {/* ── USPS APV: manifest vs assessed ── */}
          {census.length > 0 && (
            <section>
              <h2 className="text-[15px] font-bold text-ink-800 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-royal-500 inline-block" />
                USPS APV — Manifest vs Assessed
                <span className="ml-1 px-2 py-0.5 rounded-full bg-royal-100 text-royal-700 text-[11px] font-bold">{census.length}</span>
              </h2>
              <div className="space-y-3">
                {census.map((c, i) => {
                  const delta = c.transaction_amount ?? ((c.assessed_postage ?? 0) - (c.paid_postage ?? 0))
                  return (
                    <Card key={i} className="overflow-hidden">
                      <div className="px-4 py-2 bg-ink-50 border-b border-ink-100 flex items-center justify-between text-[12px]">
                        <span className="text-ink-600">{c.transaction_date ? c.transaction_date.slice(0, 10) : '—'} · reason: <b className="text-ink-800">{c.details ?? '—'}</b></span>
                        <span className="num font-semibold text-flame-600">+{usd(delta)}</span>
                      </div>
                      <table className="w-full text-[12.5px]">
                        <thead>
                          <tr className="text-[10.5px] uppercase tracking-wider text-ink-400 border-b border-ink-100">
                            <th className="text-left px-4 py-1.5">Field</th>
                            <th className="text-left px-4 py-1.5">Manifest (declared)</th>
                            <th className="text-left px-4 py-1.5">Assessed (USPS)</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-ink-100">
                          <tr><td className="px-4 py-1.5 text-ink-500">Mail class</td><td className="px-4 py-1.5">{c.manifest_mail_class ?? '—'}</td><td className="px-4 py-1.5">{c.assessed_mail_class ?? '—'}</td></tr>
                          <tr><td className="px-4 py-1.5 text-ink-500">Weight (lb)</td><td className="px-4 py-1.5 num">{n(c.manifest_weight_lbs)}</td><td className="px-4 py-1.5 num font-semibold">{n(c.assessed_weight_lbs)}</td></tr>
                          <tr><td className="px-4 py-1.5 text-ink-500">Dimensions (in)</td><td className="px-4 py-1.5 num">{dimsOf(c.manifest_length, c.manifest_width, c.manifest_height)}</td><td className="px-4 py-1.5 num font-semibold">{dimsOf(c.assessed_length, c.assessed_width, c.assessed_height)}</td></tr>
                          <tr><td className="px-4 py-1.5 text-ink-500">Dim weight (lb)</td><td className="px-4 py-1.5 num">—</td><td className="px-4 py-1.5 num">{n(c.dimensional_weight_lbs)}</td></tr>
                          <tr><td className="px-4 py-1.5 text-ink-500">Zone</td><td className="px-4 py-1.5">{c.manifested_zone ?? '—'}</td><td className="px-4 py-1.5">{c.assessed_zone ?? '—'}</td></tr>
                          <tr><td className="px-4 py-1.5 text-ink-500">Postage</td><td className="px-4 py-1.5 num">{usd(c.paid_postage)}</td><td className="px-4 py-1.5 num font-semibold">{usd(c.assessed_postage)}</td></tr>
                        </tbody>
                      </table>
                      <div className="px-4 py-2 border-t border-ink-100 text-[11.5px] text-ink-500">
                        Scan source — weight: <b className="text-ink-700">{c.weight_scan_source ?? '—'}</b> · dimensions: <b className="text-ink-700">{c.dimensions_scan_source ?? '—'}</b>
                        {' · '}EFN <span className="mono">{c.efn ?? '—'}</span> · EPS txn <span className="mono">{c.eps_transaction_id ?? '—'}</span>
                      </div>
                    </Card>
                  )
                })}
              </div>
            </section>
          )}

          {/* ── Disputes ── */}
          {disputes.length > 0 && (
            <section>
              <h2 className="text-[15px] font-bold text-ink-800 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-sky-500 inline-block" />
                Disputes
                <span className="ml-1 px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 text-[11px] font-bold">{disputes.length}</span>
              </h2>
              <Card className="overflow-hidden">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="bg-ink-50 border-b border-ink-200">
                      <th className="text-left px-4 py-3 font-semibold text-ink-600">Dispute</th>
                      <th className="text-left px-4 py-3 font-semibold text-ink-600">Status</th>
                      <th className="text-left px-4 py-3 font-semibold text-ink-600">Category</th>
                      <th className="text-right px-4 py-3 font-semibold text-ink-600">Disputed</th>
                      <th className="text-right px-4 py-3 font-semibold text-ink-600">Credit</th>
                      <th className="text-left px-4 py-3 font-semibold text-ink-600">Opened</th>
                      <th className="text-left px-4 py-3 font-semibold text-ink-600">Resolved</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-100">
                    {disputes.map(d => (
                      <tr key={d.dispute_id} className="hover:bg-ink-50">
                        <td className="px-4 py-2.5 mono text-[12px] text-ink-700">D-{d.carrier}-{String(d.dispute_id).padStart(5, '0')}</td>
                        <td className="px-4 py-2.5">{statusBadge(d.status)}</td>
                        <td className="px-4 py-2.5 text-ink-600">{d.dispute_category}</td>
                        <td className="px-4 py-2.5 text-right num">{usd(d.disputed_amount)}</td>
                        <td className="px-4 py-2.5 text-right num">{d.credit_amount != null ? usd(d.credit_amount) : '—'}</td>
                        <td className="px-4 py-2.5 text-ink-500">{d.detected_at ? d.detected_at.slice(0, 10) : '—'}</td>
                        <td className="px-4 py-2.5 text-ink-500">{d.resolved_at ? d.resolved_at.slice(0, 10) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </section>
          )}

          {/* ── Rebills ── */}
          <section>
            <h2 className="text-[15px] font-bold text-ink-800 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-flame-500 inline-block" />
              Rebills / Adjustments
              {rebills.length > 0 && (
                <span className="ml-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[11px] font-bold">
                  {rebills.length}
                </span>
              )}
            </h2>
            {rebills.length === 0 ? (
              <Card className="p-6 text-[13px] text-ink-500 italic">
                No rebill or adjustment records found for this tracking number.
              </Card>
            ) : (
              <Card className="overflow-hidden">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="bg-ink-50 border-b border-ink-200">
                      <th className="text-left px-4 py-3 font-semibold text-ink-600">Carrier</th>
                      <th className="text-left px-4 py-3 font-semibold text-ink-600">Code</th>
                      <th className="text-left px-4 py-3 font-semibold text-ink-600">Category</th>
                      <th className="text-right px-4 py-3 font-semibold text-ink-600">Carrier Cost</th>
                      <th className="text-right px-4 py-3 font-semibold text-ink-600">Rebill Amt</th>
                      <th className="text-left px-4 py-3 font-semibold text-ink-600">Status</th>
                      <th className="text-left px-4 py-3 font-semibold text-ink-600">Invoice</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-100">
                    {rebills.map(r => (
                      <tr key={r.rebill_id} className="hover:bg-ink-50">
                        <td className="px-4 py-2.5 font-medium text-ink-800">{r.carrier}</td>
                        <td className="px-4 py-2.5 font-mono text-[12px] text-ink-700">{r.adjustment_code}</td>
                        <td className="px-4 py-2.5 text-ink-600">{r.adjustment_name ?? r.adjustment_category}</td>
                        <td className="px-4 py-2.5 text-right num text-ink-700">
                          ${Number(r.cost_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-2.5 text-right num font-semibold text-ink-900">
                          ${Number(r.rebill_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-2.5">{statusBadge(r.status)}</td>
                        <td className="px-4 py-2.5 font-mono text-[11px] text-ink-400">{r.invoice_number ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Reason for first rebill */}
                {rebills.length === 1 && rebills[0].rebill_reason && (
                  <div className="px-4 py-3 border-t border-ink-100 bg-ink-50">
                    <div className="text-[11px] font-semibold text-ink-400 uppercase tracking-wider mb-1">Reason</div>
                    <div className="text-[13px] text-ink-700">{rebills[0].rebill_reason}</div>
                  </div>
                )}
              </Card>
            )}
          </section>

        </div>
      )}
    </div>
  )
}

// ===========================================================================
// Bulk lookup page — search box (one or many) / file upload → results table →
// click a row to open the detail drawer. Exportable.
// ===========================================================================
const SUMMARY_COLS = [
  'tracking_number', 'master_tracking_number', 'found', 'carrier', 'customer', 'ship_date',
  'bl_cost', 'bl_profit', 'bl_tax', 'bl_total',
  'carrier_transport', 'carrier_duty', 'carrier_total', 'transport_pl', 'duty_pl', 'total_pl', 'apv_adjustment',
  'dispute_status', 'rebill_status', 'invoiced',
] as const

function splitTrackings(text: string): string[] {
  return [...new Set(text.split(/[\s,;]+/).map(s => s.trim()).filter(s => s.length >= 5))]
}

export function LookupView() {
  const { currency } = useCurrency()
  const [input, setInput] = useState('')
  const [rows, setRows] = useState<BulkResult[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [detail, setDetail] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  async function runBulk(trackings: string[]) {
    if (trackings.length === 0) { setErr('Enter at least one tracking number.'); return }
    setLoading(true); setErr(null)
    try {
      const r = await fetch('/api/lookup/bulk', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trackings }),
      })
      const j = await r.json()
      if (!r.ok) throw new Error(j?.error || 'Lookup failed')
      setRows(j.results as BulkResult[])
    } catch (e) { setErr((e as Error).message); setRows(null) }
    finally { setLoading(false) }
  }

  async function onUpload(file: File) {
    setLoading(true); setErr(null)
    try {
      const fd = new FormData(); fd.append('file', file)
      const r = await fetch('/api/lookup/parse', { method: 'POST', body: fd })
      const j = await r.json()
      if (!r.ok) throw new Error(j?.error || 'Could not read the file')
      const tns: string[] = j.trackings ?? []
      setInput(tns.join('\n'))
      if (tns.length) await runBulk(tns)
      else setErr('No tracking numbers found in that file.')
    } catch (e) { setErr((e as Error).message) }
    finally { setLoading(false); if (fileRef.current) fileRef.current.value = '' }
  }

  function exportSummaryCsv() {
    if (!rows?.length) return
    const esc = (v: unknown) => {
      const s = v == null ? '' : String(v)
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
    }
    const csv = [SUMMARY_COLS.join(',')]
      .concat(rows.map(r => SUMMARY_COLS.map(c => esc((r as unknown as Record<string, unknown>)[c])).join(',')))
      .join('\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
    const a = document.createElement('a')
    a.href = url; a.download = `tracking-lookup-${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a); a.click(); a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 10_000)
  }

  async function exportFull() {
    if (!rows?.length) return
    setExporting(true); setErr(null)
    let url: string | null = null
    try {
      const res = await fetch('/api/lookup/export', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trackings: rows.map(r => r.tracking) }),
      })
      if (!res.ok) throw new Error((await res.json())?.error || 'Export failed')
      const blob = await res.blob()
      url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = `tracking-lookup-${new Date().toISOString().slice(0, 10)}.xlsx`
      document.body.appendChild(a); a.click(); a.remove()
    } catch (e) { setErr((e as Error).message) }
    finally { setExporting(false); if (url) setTimeout(() => URL.revokeObjectURL(url!), 10_000) }
  }

  const trackings = splitTrackings(input)
  const money = (v: number | null, cur = 'USD') =>
    v == null ? '—' : `${cur === 'GBP' ? '£' : cur === 'CAD' ? 'C$' : '$'}${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

  return (
    <div>
      <div className="mb-5">
        <div className="text-[12px] font-semibold text-royal-600 uppercase tracking-wider">Reconciliation</div>
        <h1 className="text-[28px] font-bold tracking-tight mt-1">Tracking Lookup</h1>
        <p className="text-ink-500 text-[13px] mt-1">
          One tracking number or many — paste a list or upload a CSV/Excel. Click any row for full detail.
        </p>
      </div>

      <Card className="p-4 mb-4">
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="Paste one or many tracking numbers (any separator), e.g.&#10;9205590381124800073737&#10;AS014103527US"
          rows={Math.min(6, Math.max(2, input.split('\n').length))}
          className="w-full border border-ink-200 rounded-lg px-3 py-2 text-[13px] font-mono bg-white text-ink-900 placeholder-ink-400 focus:outline-none focus:ring-2 focus:ring-royal-300 resize-y"
        />
        <div className="flex items-center gap-2 mt-2">
          <button
            onClick={() => runBulk(trackings)}
            disabled={loading || trackings.length === 0}
            className="px-4 py-2 bg-royal-600 text-white text-[13px] font-semibold rounded-lg hover:bg-royal-700 disabled:opacity-40">
            {loading ? 'Looking up…' : `Look up${trackings.length ? ` (${trackings.length})` : ''}`}
          </button>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={loading}
            className="px-3 py-2 border border-ink-200 text-ink-700 text-[13px] font-medium rounded-lg hover:bg-ink-50 inline-flex items-center gap-1.5 disabled:opacity-40">
            <Icon name="cloud-upload" className="w-3.5 h-3.5" /> Upload CSV/Excel
          </button>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
                 onChange={e => { const f = e.target.files?.[0]; if (f) onUpload(f) }} />
          {rows && rows.length > 0 && (
            <div className="ml-auto flex items-center gap-2">
              <button onClick={exportSummaryCsv}
                className="px-3 py-2 border border-ink-200 text-ink-700 text-[13px] font-medium rounded-lg hover:bg-ink-50 inline-flex items-center gap-1.5">
                <Icon name="file-spreadsheet" className="w-3.5 h-3.5" /> Summary CSV
              </button>
              <button onClick={exportFull} disabled={exporting}
                className="px-3 py-2 bg-mint-500 text-white text-[13px] font-semibold rounded-lg hover:bg-mint-600 inline-flex items-center gap-1.5 disabled:opacity-40">
                <Icon name="download" className="w-3.5 h-3.5" /> {exporting ? 'Building…' : 'Full detail (Excel)'}
              </button>
            </div>
          )}
        </div>
        {err && <div className="mt-2 text-[12.5px] text-red-600">{err}</div>}
      </Card>

      {rows && (
        <Card className="overflow-hidden">
          <div className="px-4 py-2.5 border-b border-ink-100 text-[12.5px] text-ink-500">
            {rows.length} result{rows.length === 1 ? '' : 's'} · {rows.filter(r => !r.found).length} not found
          </div>
          <div className="overflow-auto max-h-[70vh]">
            <table className="w-full text-[12.5px] min-w-[1850px]">
              <thead className="bg-ink-50 text-ink-500 text-[10.5px] uppercase tracking-wider sticky top-0 z-10">
                <tr className="bg-ink-50 [&>th]:bg-ink-50 shadow-[0_1px_0_0] shadow-ink-200">
                  <th className="text-left font-semibold py-2 px-4">Tracking #</th>
                  <th className="text-left font-semibold py-2">Master tracking #</th>
                  <th className="text-left font-semibold py-2">Carrier</th>
                  <th className="text-left font-semibold py-2">Customer</th>
                  <th className="text-left font-semibold py-2">Ship date</th>
                  <th className="text-right font-semibold py-2">BL cost</th>
                  <th className="text-right font-semibold py-2">BL profit</th>
                  <th className="text-right font-semibold py-2">BL tax</th>
                  <th className="text-right font-semibold py-2">BL total</th>
                  <th className="text-right font-semibold py-2">Carrier transport</th>
                  <th className="text-right font-semibold py-2">Carrier duty</th>
                  <th className="text-right font-semibold py-2">Carrier total</th>
                  <th className="text-right font-semibold py-2">Transport P/L</th>
                  <th className="text-right font-semibold py-2">Duty/tax P/L</th>
                  <th className="text-right font-semibold py-2" title="BL total − carrier total (duty not yet billed by the carrier counts as 0)">Total P/L</th>
                  <th className="text-right font-semibold py-2">APV adj.</th>
                  <th className="text-left font-semibold py-2 pl-3">Dispute</th>
                  <th className="text-left font-semibold py-2">Rebill</th>
                  <th className="text-left font-semibold py-2 pr-4">Inv.</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.tracking}
                    onClick={() => setDetail(r.tracking)}
                    className={`border-t border-ink-100 cursor-pointer hover:bg-ink-50 ${!r.found ? 'opacity-60' : ''} ${detail === r.tracking ? 'bg-royal-50' : ''}`}>
                    <td className="py-2 px-4 mono text-[12px] text-ink-800 max-w-[230px] truncate" title={r.tracking_number ?? r.tracking}>
                      {r.tracking_number ?? r.tracking}{!r.found && <span className="ml-2 text-[10px] text-red-500 font-semibold">NOT FOUND</span>}
                    </td>
                    <td className="py-2 mono text-[12px] text-ink-600 max-w-[190px] truncate" title={r.master_tracking_number ?? ''}>{r.master_tracking_number ?? '—'}</td>
                    <td className="py-2 text-ink-600">{r.carrier ?? '—'}</td>
                    <td className="py-2 text-ink-600 max-w-[160px] truncate" title={r.customer ?? ''}>{r.customer ?? '—'}</td>
                    <td className="py-2 text-ink-500 num">{r.ship_date ? r.ship_date.slice(0, 10) : '—'}</td>
                    <td className="py-2 text-right num">{money(r.bl_cost)}</td>
                    <td className={`py-2 text-right num ${(r.bl_profit ?? 0) < 0 ? 'text-red-600' : 'text-mint-700'}`}>{money(r.bl_profit)}</td>
                    <td className="py-2 text-right num">{money(r.bl_tax)}</td>
                    <td className="py-2 text-right num">{money(r.bl_total)}</td>
                    <td className="py-2 text-right num">{money(r.carrier_transport)}</td>
                    <td className="py-2 text-right num">{r.carrier_duty == null ? '—' : money(r.carrier_duty)}</td>
                    <td className="py-2 text-right num">{money(r.carrier_total)}</td>
                    <td className={`py-2 text-right num font-semibold ${r.transport_pl == null ? '' : r.transport_pl < 0 ? 'text-red-600' : 'text-mint-700'}`}>{r.transport_pl == null ? '—' : money(r.transport_pl)}</td>
                    <td className={`py-2 text-right num font-semibold ${r.duty_pl == null ? '' : r.duty_pl < 0 ? 'text-red-600' : 'text-mint-700'}`}>{r.duty_pl == null ? '—' : money(r.duty_pl)}</td>
                    <td className={`py-2 text-right num font-bold ${r.total_pl == null ? '' : r.total_pl < 0 ? 'text-red-600' : 'text-mint-700'}`}>{r.total_pl == null ? '—' : money(r.total_pl)}</td>
                    <td className={`py-2 text-right num ${(r.apv_adjustment ?? 0) > 0 ? 'text-flame-600 font-semibold' : ''}`}>{r.apv_adjustment != null ? money(r.apv_adjustment) : '—'}</td>
                    <td className="py-2 pl-3">{r.dispute_status ? <span className="pill pill-sky">{r.dispute_status}</span> : '—'}</td>
                    <td className="py-2">{r.rebill_status ? <span className="text-ink-600">{r.rebill_status}</span> : '—'}</td>
                    <td className="py-2 pr-4">{r.invoiced ? <span className="text-mint-700 font-semibold">✓</span> : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {!rows && !loading && (
        <div className="text-center py-16 text-ink-400 text-[14px]">
          Paste tracking numbers or upload a file to begin.
        </div>
      )}

      {/* Detail drawer */}
      {detail && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setDetail(null)} />
          <div className="fixed top-0 right-0 h-full w-[560px] max-w-[92vw] z-50 bg-white border-l border-ink-200 shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-ink-100 shrink-0">
              <div>
                <div className="text-[13px] font-semibold text-ink-900 mono break-all">{detail}</div>
                <div className="text-[11.5px] text-ink-500">Shipment detail</div>
              </div>
              <button onClick={() => setDetail(null)} className="text-ink-400 hover:text-ink-700 shrink-0"><Icon name="x" className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <ShipmentDetail tracking={detail} />
            </div>
          </div>
        </>
      )}
    </div>
  )
}
